'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';

interface Domain {
  id: string;
  domain: string;
  status: 'pending' | 'active' | 'rejected' | 'suspended';
  mx_verified: boolean;
  spf_verified: boolean;
  dkim_verified: boolean;
  catch_all_enabled: boolean;
  forwarding_email: string;
  verification_token: string;
  applied_at: string;
  notes: string;
}

type ToastType = 'success' | 'error' | 'info';
interface Toast { id: number; msg: string; type: ToastType }

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = (msg: string, type: ToastType = 'success') => {
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  };
  return { toasts, show };
}

const DNS_RECORDS = (domain: string, token: string) => [
  { type: 'MX', name: '@', value: 'mx.companyinbox.online', priority: 10, label: 'Mail routing' },
  { type: 'TXT', name: '@', value: `v=spf1 include:companyinbox.online ~all`, priority: null, label: 'SPF' },
  { type: 'TXT', name: '_dmarc', value: `v=DMARC1; p=quarantine; rua=mailto:dmarc@companyinbox.online`, priority: null, label: 'DMARC' },
  { type: 'TXT', name: `_ci-verify.${domain || 'yourdomain.com'}`, value: token || 'ci-verify-token-pending', priority: null, label: 'Verification' },
];

export default function DomainsPage() {
  const { user, loading } = useAuth();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const [copiedRecord, setCopiedRecord] = useState<string | null>(null);
  const [updatingCatchAll, setUpdatingCatchAll] = useState<string | null>(null);
  const { toasts, show } = useToast();
  const supabase = createClient();

  useEffect(() => {
    if (user) fetchDomains();
    else if (!loading) setIsLoading(false);
  }, [user, loading]);

  const fetchDomains = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('user_id', user!.id)
        .order('applied_at', { ascending: false });
      if (!error) setDomains(data ?? []);
    } catch {
      show('Failed to load domains', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const generateToken = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    return 'ci-' + Array.from({ length: 16 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain.trim())) {
      show('Please enter a valid domain (e.g. mycompany.com)', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = generateToken();
      const { error } = await supabase.from('domains').insert({
        user_id: user!.id,
        domain: newDomain.trim().toLowerCase(),
        status: 'pending',
        verification_token: token,
      });
      if (error) {
        if (error.code === '23505') show('This domain is already registered', 'error');
        else show('Failed to apply for domain', 'error');
        return;
      }
      show(`Domain submitted! Add the DNS records below to verify.`, 'success');
      setNewDomain('');
      fetchDomains();
    } catch {
      show('Something went wrong. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyRecord = (value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedRecord(value);
      show('Copied to clipboard');
      setTimeout(() => setCopiedRecord(null), 2000);
    });
  };

  const toggleCatchAll = async (domainId: string, current: boolean) => {
    setUpdatingCatchAll(domainId);
    try {
      const { error } = await supabase
        .from('domains')
        .update({ catch_all_enabled: !current })
        .eq('id', domainId)
        .eq('user_id', user!.id);
      if (error) throw error;
      setDomains((prev) => prev.map((d) => d.id === domainId ? { ...d, catch_all_enabled: !current } : d));
      show(!current ? 'Catch-all enabled — all addresses at this domain will route to your inbox' : 'Catch-all disabled', 'info');
    } catch {
      show('Failed to update catch-all setting', 'error');
    } finally {
      setUpdatingCatchAll(null);
    }
  };

  const statusConfig = {
    pending: { label: 'Pending DNS', color: 'text-amber-600 bg-amber-50 border-amber-200' },
    active: { label: 'Active', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    rejected: { label: 'Rejected', color: 'text-red-600 bg-red-50 border-red-200' },
    suspended: { label: 'Suspended', color: 'text-red-600 bg-red-50 border-red-200' },
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center">
        <div className="glass-card p-8 rounded-2xl flex items-center gap-3">
          <svg className="animate-spin w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-foreground font-medium">Loading domains...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center">
        <div className="glass-card p-8 rounded-2xl text-center max-w-sm">
          <p className="text-foreground font-semibold mb-4">Sign in to manage your domains</p>
          <Link href="/sign-up-login-screen" className="btn-primary">Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mesh">
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div key={t.id} className={`px-4 py-3 rounded-xl text-sm font-medium shadow-lg border backdrop-blur-md animate-slide-up pointer-events-auto
            ${t.type === 'success' ? 'bg-emerald-50/90 text-emerald-700 border-emerald-200' :
              t.type === 'error'? 'bg-red-50/90 text-red-700 border-red-200' : 'bg-blue-50/90 text-blue-700 border-blue-200'}`}>
            {t.msg}
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="glass-topbar px-6 py-4 flex items-center gap-4 sticky top-0 z-30">
        <Link href="/inbox-dashboard" className="flex items-center gap-2">
          <div className="glass rounded-lg p-1.5"><AppLogo size={22} /></div>
          <span className="font-bold text-foreground">CompanyInbox</span>
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground font-semibold">Custom Domains</span>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/secure-paste" className="btn-ghost text-sm py-1.5 px-3 flex items-center gap-1.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
            </svg>
            SecurePaste
          </Link>
          <Link href="/inbox-dashboard" className="btn-ghost text-sm py-1.5 px-3">← Back to Inbox</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Hero */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/8 border border-primary/15 text-xs font-semibold text-primary mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            Bring Your Own Domain
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Custom Domain Email</h1>
          <p className="text-muted-foreground max-w-xl">
            Route all mail for <span className="font-mono text-primary">*@yourcompany.com</span> through CompanyInbox. Your address looks completely legitimate — no spam flags, no temp-mail detection.
          </p>
        </div>

        {/* How it works banner */}
        <div className="glass rounded-2xl p-4 mb-6 border border-primary/10">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B4FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div className="text-sm text-secondary-foreground leading-relaxed">
              <strong className="text-foreground">How it works:</strong> Add the DNS records below to your registrar (Cloudflare, Namecheap, GoDaddy, etc.). Once verified, every email sent to <em>any</em> address at your domain lands in your CompanyInbox inbox. Enable catch-all to capture everything — even addresses you haven&apos;t created yet.
            </div>
          </div>
        </div>

        {/* Apply form */}
        <div className="glass-card rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-bold text-foreground mb-1">Add a domain</h2>
          <p className="text-sm text-muted-foreground mb-4">Enter your domain name to get your personalized DNS records.</p>
          <form onSubmit={handleApply} className="flex gap-3">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="yourcompany.com"
              className="input-field flex-1"
            />
            <button type="submit" className="btn-primary px-6" disabled={isSubmitting}>
              {isSubmitting ? (
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : 'Add Domain'}
            </button>
          </form>
        </div>

        {/* Domain list */}
        <div className="space-y-4">
          {domains.length === 0 ? (
            <div className="glass-card rounded-2xl p-10 text-center">
              <div className="w-12 h-12 rounded-2xl bg-primary/8 border border-primary/15 flex items-center justify-center mx-auto mb-4">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B4FE8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                </svg>
              </div>
              <p className="font-semibold text-foreground mb-1">No domains yet</p>
              <p className="text-sm text-muted-foreground">Add your first domain above to get started.</p>
            </div>
          ) : (
            domains.map((domain) => {
              const isExpanded = expandedDomain === domain.id;
              const records = DNS_RECORDS(domain.domain, domain.verification_token);
              const allVerified = domain.mx_verified && domain.spf_verified && domain.dkim_verified;
              const cfg = statusConfig[domain.status];

              return (
                <div key={domain.id} className="glass-card rounded-2xl overflow-hidden">
                  {/* Domain header row */}
                  <div className="p-5 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono font-bold text-foreground text-base">{domain.domain}</span>
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {domain.catch_all_enabled && (
                          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border text-violet-600 bg-violet-50 border-violet-200">
                            Catch-all ON
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        {[
                          { label: 'MX', ok: domain.mx_verified },
                          { label: 'SPF', ok: domain.spf_verified },
                          { label: 'DKIM', ok: domain.dkim_verified },
                        ].map((v) => (
                          <div key={v.label} className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-full ${v.ok ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                            <span className={`text-xs font-medium ${v.ok ? 'text-emerald-600' : 'text-muted-foreground'}`}>{v.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedDomain(isExpanded ? null : domain.id)}
                      className="flex-shrink-0 p-2 rounded-xl hover:bg-white/60 transition-colors text-muted-foreground hover:text-foreground"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>
                  </div>

                  {/* Expanded DNS + settings */}
                  {isExpanded && (
                    <div className="border-t border-white/50 p-5 space-y-5">
                      {/* DNS Records */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-bold text-foreground">DNS Records</h3>
                          {allVerified && (
                            <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                              All records verified
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">Add these records at your DNS provider. Changes can take up to 48 hours to propagate.</p>
                        <div className="space-y-2">
                          {records.map((record, i) => (
                            <div key={`r-${i}`} className="glass rounded-xl p-3 flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">{record.type}</span>
                                  <span className="text-xs font-mono text-foreground">{record.name}</span>
                                  {record.priority && <span className="text-xs text-muted-foreground">Priority: {record.priority}</span>}
                                  <span className="text-xs text-muted-foreground">({record.label})</span>
                                </div>
                                <p className="text-xs font-mono text-muted-foreground break-all">{record.value}</p>
                              </div>
                              <button
                                onClick={() => copyRecord(record.value)}
                                className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/60 transition-colors text-muted-foreground hover:text-foreground"
                                title="Copy value"
                              >
                                {copiedRecord === record.value ? (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                ) : (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                                  </svg>
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Catch-all toggle */}
                      <div className="glass rounded-xl p-4 flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B4FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                              <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <span className="text-sm font-bold text-foreground">Catch-all routing</span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            When enabled, <em>any</em> email sent to <span className="font-mono text-primary">*@{domain.domain}</span> — even addresses you haven&apos;t created — will land in your inbox. Perfect for <span className="font-mono">hello@</span>, <span className="font-mono">noreply@</span>, or anything else.
                          </p>
                        </div>
                        <button
                          onClick={() => toggleCatchAll(domain.id, domain.catch_all_enabled)}
                          disabled={updatingCatchAll === domain.id || domain.status !== 'active'}
                          className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-all duration-200 focus:outline-none
                            ${domain.catch_all_enabled ? 'bg-primary' : 'bg-gray-200'}
                            ${domain.status !== 'active' ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                          title={domain.status !== 'active' ? 'Domain must be active to enable catch-all' : ''}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                            ${domain.catch_all_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                      </div>

                      {domain.status === 'pending' && (
                        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                          <strong>Awaiting verification:</strong> Add the DNS records above, then our team will verify and activate your domain within 24 hours.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
